import { forwardRef } from 'react';
import { ICON_BLUE_PATHS, ICON_GREEN_PATHS } from './idgateIconPaths';

// Icon (pole, boom arm, checkmark) is built from ~290 real vector paths
// traced from the shipped logo (public/logos/logo-final.svg via VTracer),
// not hand-drawn — see idgateIconPaths.js. Each path is recolored by
// interpolating between two user-editable endpoint colors using its
// original brightness position, preserving the source art's shading with
// only 4 icon color fields instead of ~290 fixed shades. Wordmark and
// slogan stay live Philosopher text (not traced) so kerning, embedded-font
// export, and per-letter recoloring keep working.
const mixColor = (t, colorA, colorB) => {
  const a = parseInt(colorA.slice(1), 16);
  const b = parseInt(colorB.slice(1), 16);
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bch = Math.round(ab + (bb - ab) * t);
  return `#${((1 << 24) + (r << 16) + (g << 8) + bch).toString(16).slice(1)}`;
};

const IdgateLogoSVG = forwardRef(({ colors: c, uid = 'logo', width = '100%', style }, ref) => {
  const id = (name) => `${name}-${uid}`;
  const u = (name) => `url(#${id(name)})`;

  return (
    <svg
      ref={ref}
      viewBox="0 0 1596 732"
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      style={{ display: 'block', ...style }}
    >
      <defs>
        <linearGradient id={id('gateGrad')} x1="902" y1="328" x2="1466" y2="560" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={c.gate1} />
          <stop offset="1" stopColor={c.gate2} />
        </linearGradient>
      </defs>

      {!c.transparent && <rect x="0" y="0" width="1596" height="732" fill={c.bg} />}

      {/* Icon: traced pole + boom arm paths, recolored along the blueDark/blueLight axis */}
      {ICON_BLUE_PATHS.map((p, i) => (
        <path key={`b${i}`} d={p.d} fill={mixColor(p.t, c.blueDark, c.blueLight)} transform={`translate(${p.tx} ${p.ty})`} />
      ))}
      {/* Icon: traced checkmark paths, recolored along the greenDark/greenLight axis */}
      {ICON_GREEN_PATHS.map((p, i) => (
        <path key={`g${i}`} d={p.d} fill={mixColor(p.t, c.greenDark, c.greenLight)} transform={`translate(${p.tx} ${p.ty})`} />
      ))}

      {/* Wordmark */}
      <text
        x="620"
        y="531"
        fontFamily="'Philosopher', serif"
        fontWeight="700"
        fontSize="267"
        letterSpacing="-8"
        fill={c.id1}
      >
        ID
      </text>
      <text
        x="902"
        y="531"
        fontFamily="'Philosopher', serif"
        fontWeight="700"
        fontSize="267"
        letterSpacing="-8"
        fill={u('gateGrad')}
      >
        Gate
      </text>

      {/* Slogan — each word independently colorable */}
      <text
        x="628"
        y="621"
        fontFamily="'Philosopher', serif"
        fontStyle="italic"
        fontWeight="400"
        fontSize="79"
        letterSpacing="4"
      >
        <tspan fill={c.slogan1}>Scan.</tspan>
        <tspan fill={c.slogan2}>Verify.</tspan>
        <tspan fill={c.slogan3}>Check-in</tspan>
      </text>
    </svg>
  );
});

IdgateLogoSVG.displayName = 'IdgateLogoSVG';

export default IdgateLogoSVG;
